import type { FC } from 'react'
import { useState } from 'react'
import 'swiper/css'
import { CampaignSiteLayout } from '~/components/campaign/CampaignSiteLayout'
import MuleProjectUpload from '~/components/campaign/MuleProjectUpload'
import { CurieSelect } from '~/components/common/CurieSelect'
import { BaseTaskForm, TaskParams } from '~/components/task-forms/BaseTaskForm'
import { AgentRequest, CodeContext, CommandType, Metadata } from '~/protos/agents'
import { trace } from '~/services/analyticsService'
import { BetterToast } from '~/utils/toast'

const flowNameExamples = [
  {
    label: 'Order Processing Flow',
    key: '0',
    flowName: 'order-processing-flow'
  },
  {
    label: 'Customer Onboarding Flow',
    key: '1',
    flowName: 'customer-onboarding-flow'
  },
  {
    label: 'Payment Gateway Integration',
    key: '2',
    flowName: 'payment-gateway-integration-flow'
  }
]

const Page: FC = () => {
  const [request, setRequest] = useState<AgentRequest>(AgentRequest.fromPartial({}))
  const [example, setExample] = useState<string[]>([flowNameExamples[0].key])
  const [uploadedPaths, setUploadedPaths] = useState<string[]>([])
  const [defaultFlowName, setDefaultFlowName] = useState<string | undefined>(
    flowNameExamples[0].flowName
  )

  const checkFieldsFilled = (request: AgentRequest) => {
    if (uploadedPaths.length === 0) {
      BetterToast.error('Please select a Mule project')
      trace('campaign_task_fields_not_filled')
      return false
    }
    const flowName = request.command?.commandString
    if (flowName == null || flowName.trim().length === 0) {
      BetterToast.error('Please provide a flow name')
      trace('campaign_task_fields_not_filled')
      return false
    }
    return true
  }

  const checkIfExampleRequest = (request: AgentRequest) => {
    const flowName = request.command?.commandString
    return flowNameExamples.some(example => example.flowName.trim() === flowName?.trim())
  }

  const buildRequest = () => {
    request.metadata = Metadata.fromPartial({
      codeContext: CodeContext.fromPartial({
        connType: 'S3',
        s3TarFilePath: uploadedPaths[0]
      })
    })
    return request
  }

  return (
    <CampaignSiteLayout
      title='Flow Documentation Generator'
      description='Use Generative AI to generate documentation for your Mule Flow/API'
      command={CommandType.GENERATE_DOCUMENTATION}
      checkFieldsFilled={checkFieldsFilled}
      checkIfExampleRequest={checkIfExampleRequest}
      request={buildRequest()}
      instructions={
        <ol className='pl-2'>
          <li className='list-decimal list-inside'>
            Start by selecting one of the example flow names below, or enter your own flow name.
          </li>
          <li className='list-decimal list-inside'>
            Click generate to see Curie create documentation based on the flow name.
          </li>
        </ol>
      }
      examples={
        <div className='w-full flex mt-4 mb-8'>
          <div className='display flex items-center text-sm text-default-500'>
            <span className='mr-2'>Examples:</span>
            <CurieSelect
              placeholder='Select an example'
              className='w-64 pb-0'
              selectedKeys={example}
              items={flowNameExamples.map(ex => ({ label: ex.label, key: ex.key }))}
              onSelectionChange={selectedKeys => {
                if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
                  return
                }
                const key = selectedKeys.values().next().value as string
                setExample([key])
                const selectedExample = flowNameExamples.find(ex => ex.key === key)
                if (selectedExample) {
                  setDefaultFlowName(selectedExample.flowName)
                }
              }}
            />
          </div>
        </div>
      }
    >
      <MuleProjectUpload onUploadComplete={setUploadedPaths} />
      <BaseTaskForm
        key={String(defaultFlowName)}
        label='Flow Name'
        placeholder='Enter your Mule flow name'
        minRows={1}
        maxRows={1}
        onChange={(task: TaskParams) => setRequest(task.request)}
        defaultValue={defaultFlowName}
        inputClassName='h-[40px]'
      />
    </CampaignSiteLayout>
  )
}

export default Page
